import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Socket } from 'ngx-socket-io';
import { firstValueFrom } from 'rxjs';
import {
  CellLabelDto,
  ClientInvoicePaymentReceiptRow,
  ClientInvoicePaymentRow,
  ConnectedWorkstationAgent,
  CreateLabelProfileDto,
  CreateWorkstationPrinterDto,
  ExportDoc,
  Order,
  PriceTagDto,
  LabelProfileRef,
  PrinterTarget,
  PrinterTargetRole,
  PrintTargetSelector,
  ProductLabelPrintDto,
  TsplLabelRef,
  UpdateLabelProfileDto,
  UpdateWorkstationPrinterDto,
  User
} from 'src/typings';
import { WorkstationPrinterRecord } from 'src/typings';
import { environment } from 'src/environments/environment';
import { NotificationService } from '../services/notification.service';
import { UserService } from '../services/user.service';

@Injectable({
  providedIn: 'root'
})
export class ComputerService {
  user: User | undefined;

  constructor(
    private socket: Socket,
    private userService: UserService,
    private http: HttpClient,
    private notificationService: NotificationService
  ) {
    this.userService.user.subscribe((user) => {
      this.user = user;
    });
  }

  async printOrder(payload: { order: Order; withArticles: boolean; agentName?: string }) {
    if (!this.user)
      return this.notificationService.warning(
        `Р СњР Вµ Р СР С•Р С–РЎС“ Р Р…Р В°Р в„–РЎвЂљР С‘ Р С—Р С•Р В»РЎРЉР В·Р С•Р Р†Р В°РЎвЂљР ВµР В»РЎРЏ!`
      );
    await firstValueFrom(
      this.http.post(`${environment.BACKEND}/thermal-print/order`, {
        order: this.normalizeOrderForThermalPrint(payload.order),
        withArticles: payload.withArticles,
        agentName: payload.agentName
      })
    );
  }

  printExportDoc(doc: ExportDoc, agentName?: string) {
    if (!this.user)
      return this.notificationService.warning(
        `Р СњР Вµ Р СР С•Р С–РЎС“ Р Р…Р В°Р в„–РЎвЂљР С‘ Р С—Р С•Р В»РЎРЉР В·Р С•Р Р†Р В°РЎвЂљР ВµР В»РЎРЏ!`
      );
    return firstValueFrom(
      this.http.post(`${environment.BACKEND}/thermal-print/export-doc`, {
        ...this.normalizeExportDocForThermalPrint(doc),
        agentName
      })
    );
  }

  printAssemblySheet(payload: {
    warehouse: string;
    warehouseId?: number;
    products: Array<{
      id?: number;
      description: string;
      brand: string;
      article: string;
      quan: number;
      cell: string | undefined;
      nal: number | undefined;
      cell_allocations?: Array<{ cell_id: number; quan: number; code?: string; type?: string }>;
    }>;
    agentName?: string;
  }) {
    if (!this.user)
      return this.notificationService.warning(
        `Р СњР Вµ Р СР С•Р С–РЎС“ Р Р…Р В°Р в„–РЎвЂљР С‘ Р С—Р С•Р В»РЎРЉР В·Р С•Р Р†Р В°РЎвЂљР ВµР В»РЎРЏ!`
      );
    return firstValueFrom(
      this.http.post(`${environment.BACKEND}/thermal-print/assembly-sheet`, payload)
    );
  }

  printClientInvoicePaymentFiscalReceipt(paymentId: number, agentName?: string) {
    if (!this.user) {
      return this.notificationService.warning(
        `Р В РЎСљР В Р’Вµ Р В РЎВР В РЎвЂўР В РЎвЂ“Р РЋРЎвЂњ Р В Р вЂ¦Р В Р’В°Р В РІвЂћвЂ“Р РЋРІР‚С™Р В РЎвЂ Р В РЎвЂ”Р В РЎвЂўР В Р’В»Р РЋР Р‰Р В Р’В·Р В РЎвЂўР В Р вЂ Р В Р’В°Р РЋРІР‚С™Р В Р’ВµР В Р’В»Р РЋР РЏ!`
      );
    }

    return firstValueFrom(
      this.http.post(`${environment.BACKEND}/thermal-print/payment/${paymentId}/fiscal`, {
        agentName
      })
    );
  }

  printClientInvoicePaymentNonFiscalReceipt(paymentId: number, agentName?: string) {
    if (!this.user) {
      return this.notificationService.warning(
        `Р В РЎСљР В Р’Вµ Р В РЎВР В РЎвЂўР В РЎвЂ“Р РЋРЎвЂњ Р В Р вЂ¦Р В Р’В°Р В РІвЂћвЂ“Р РЋРІР‚С™Р В РЎвЂ Р В РЎвЂ”Р В РЎвЂўР В Р’В»Р РЋР Р‰Р В Р’В·Р В РЎвЂўР В Р вЂ Р В Р’В°Р РЋРІР‚С™Р В Р’ВµР В Р’В»Р РЋР РЏ!`
      );
    }

    return firstValueFrom(
      this.http.post(`${environment.BACKEND}/thermal-print/payment/${paymentId}/non-fiscal`, {
        agentName
      })
    );
  }

  openCashDrawer(agentName?: string) {
    return firstValueFrom(
      this.http.post<{
        requestId: string;
        dispatched: boolean;
        agentName: string;
        deviceId: string;
      }>(`${environment.BACKEND}/thermal-print/cash-drawer/open`, {
        ...(agentName ? { agentName } : {})
      })
    );
  }

  printClientInvoicePaymentReceipt(
    row: ClientInvoicePaymentRow,
    agentName?: string,
    receipt?: ClientInvoicePaymentReceiptRow | null
  ) {
    return this.resolveAndPrintClientInvoicePaymentReceipt(row, receipt, agentName);
  }

  resolveAndPrintClientInvoicePaymentReceipt(
    row: ClientInvoicePaymentRow,
    receipt?: ClientInvoicePaymentReceiptRow | null,
    agentName?: string
  ) {
    return this.canPrintFiscalPaymentReceipt(row, receipt)
      ? this.printClientInvoicePaymentFiscalReceipt(row.id, agentName)
      : this.printClientInvoicePaymentNonFiscalReceipt(row.id, agentName);
  }

  printExplicitClientInvoicePaymentReceipt(
    row: ClientInvoicePaymentRow,
    receiptType: 'fiscal' | 'non-fiscal',
    receipt?: ClientInvoicePaymentReceiptRow | null,
    agentName?: string
  ) {
    if (receiptType === 'fiscal') {
      if (!this.canPrintFiscalPaymentReceipt(row, receipt)) {
        throw new Error(
          "Фiscalный чек недоступний для друку: неповний набір обов'язкових реквізитів"
        );
      }

      return this.printClientInvoicePaymentFiscalReceipt(row.id, agentName);
    }

    return this.printClientInvoicePaymentNonFiscalReceipt(row.id, agentName);
  }

  canPrintFiscalPaymentReceipt(
    row: ClientInvoicePaymentRow,
    receipt?: ClientInvoicePaymentReceiptRow | null
  ) {
    const isFiscalized = row.receipt_status === 'SUCCESS' || row.is_fiscalized;
    return Boolean(isFiscalized && receipt?.receiptSnapshotValidation?.isComplete);
  }

  canPrintNonFiscalPaymentReceipt(row: ClientInvoicePaymentRow) {
    return Boolean(row.id && row.invoice_id);
  }

  getConnectedWorkstationAgents() {
    return this.http.get<ConnectedWorkstationAgent[]>(
      `${environment.BACKEND}/workstation-agent/connected`
    );
  }

  getPrinterTargets(
    role: PrinterTargetRole,
    warehouseId?: number | null,
    profileId?: number | string | null
  ) {
    let params = new HttpParams().set('role', role);
    if (warehouseId !== undefined && warehouseId !== null) {
      params = params.set('warehouseId', String(warehouseId));
    }
    if (profileId !== undefined && profileId !== null && `${profileId}`.trim()) {
      params = params.set('profileId', String(profileId));
    }

    return this.http.get<PrinterTarget[]>(`${environment.BACKEND}/printer-targets`, { params });
  }

  getLabelProfiles() {
    return this.http.get<LabelProfileRef[]>(`${environment.BACKEND}/label-profiles`);
  }

  createLabelProfile(payload: CreateLabelProfileDto) {
    return this.http.post<LabelProfileRef>(`${environment.BACKEND}/label-profiles`, payload);
  }

  updateLabelProfile(id: number, payload: UpdateLabelProfileDto) {
    return this.http.put<LabelProfileRef>(`${environment.BACKEND}/label-profiles/${id}`, payload);
  }

  previewProductLabelProfile(profile: LabelProfileRef | CreateLabelProfileDto) {
    return this.http.post<TsplLabelRef>(`${environment.BACKEND}/label-profiles/preview/product`, {
      profile: this.buildLabelProfilePreviewPayload(profile),
      sample: {
        article: 'OC90',
        brand: 'MAHLE',
        productName: 'Oil filter with bypass valve',
        barcode: '1234567890128',
        copies: 1
      }
    });
  }

  previewCellLabelProfile(profile: LabelProfileRef | CreateLabelProfileDto) {
    return this.http.post<TsplLabelRef>(`${environment.BACKEND}/label-profiles/preview/cell`, {
      profile: this.buildLabelProfilePreviewPayload(profile),
      sample: {
        cellCode: '5M12',
        copies: 1
      }
    });
  }

  deleteLabelProfile(id: number) {
    return this.http.delete<{ deleted: boolean; id: number }>(
      `${environment.BACKEND}/label-profiles/${id}`
    );
  }

  getWorkstationPrinters() {
    return this.http.get<WorkstationPrinterRecord[]>(`${environment.BACKEND}/workstation-printers`);
  }

  createWorkstationPrinter(payload: CreateWorkstationPrinterDto) {
    return this.http.post<WorkstationPrinterRecord>(
      `${environment.BACKEND}/workstation-printers`,
      payload
    );
  }

  updateWorkstationPrinter(id: number, payload: UpdateWorkstationPrinterDto) {
    return this.http.put<WorkstationPrinterRecord>(
      `${environment.BACKEND}/workstation-printers/${id}`,
      payload
    );
  }

  deleteWorkstationPrinter(id: number) {
    return this.http.delete<{ deleted: boolean; id: number }>(
      `${environment.BACKEND}/workstation-printers/${id}`
    );
  }

  printProductLabel(payload: ProductLabelPrintDto) {
    if (!this.user)
      return this.notificationService.warning(
        `Р СњР Вµ Р СР С•Р С–РЎС“ Р Р…Р В°Р в„–РЎвЂљР С‘ Р С—Р С•Р В»РЎРЉР В·Р С•Р Р†Р В°РЎвЂљР ВµР В»РЎРЏ!`
      );
    return firstValueFrom(
      this.http.post(
        `${environment.BACKEND}/label-print/product`,
        this.buildProductLabelRequest(payload)
      )
    );
  }

  printPriceTag(payload: PriceTagDto) {
    if (!this.user) {
      return this.notificationService.warning(
        `Р СњР Вµ Р СР С•Р С–РЎС“ Р Р…Р В°Р в„–РЎвЂљР С‘ Р С—Р С•Р В»РЎРЉР В·Р С•Р Р†Р В°РЎвЂљР ВµР В»РЎРЏ!`
      );
    }
    return firstValueFrom(
      this.http.post(
        `${environment.BACKEND}/label-print/product`,
        this.buildProductLabelRequest(payload)
      )
    );
  }

  printCellLabel(payload: CellLabelDto) {
    if (!this.user) {
      return this.notificationService.warning(
        `Р СњР Вµ Р СР С•Р С–РЎС“ Р Р…Р В°Р в„–РЎвЂљР С‘ Р С—Р С•Р В»РЎРЉР В·Р С•Р Р†Р В°РЎвЂљР ВµР В»РЎРЏ!`
      );
    }
    return firstValueFrom(
      this.http.post(`${environment.BACKEND}/label-print/cell`, this.buildCellLabelRequest(payload))
    );
  }

  private normalizeOrderForThermalPrint(order: Order) {
    const orderLike = order as Order &
      Partial<{
        date: Date | string;
        manager: string;
        client_phone: string;
        car: string;
        vin: string;
        client: string;
        prepayment: number;
      }>;

    return {
      id: order.id,
      date: orderLike.date || order.date_created,
      manager: orderLike.manager || order.user?.name || '',
      client:
        typeof orderLike.client === 'string'
          ? orderLike.client
          : [order.client?.name, order.client?.surname].filter(Boolean).join(' ').trim(),
      client_phone:
        orderLike.client_phone ||
        order.client?.phones?.find((phone) => phone.isPrimary)?.phone ||
        order.client?.phone ||
        '',
      prepayment: orderLike.prepayment,
      vin: orderLike.vin || order.client_car?.vin || '',
      car: orderLike.car || order.client_car?.description || '',
      description: order.description || '',
      products: (order.products || []).map((productRow: any) => ({
        id: productRow.id ?? productRow.product?.id,
        description: productRow.description ?? productRow.product?.description ?? '',
        brand:
          (typeof productRow.brand === 'string'
            ? productRow.brand
            : productRow.product?.brand?.description) || '',
        article: productRow.article ?? productRow.product?.article ?? '',
        quan: Number(productRow.quan || 0),
        price: Number(productRow.price || 0)
      }))
    };
  }

  private normalizeExportDocForThermalPrint(doc: ExportDoc) {
    return {
      id: doc.id,
      date: doc.date || doc.date_created,
      manager: doc.user?.name || '',
      client: [doc.client?.name, doc.client?.surname].filter(Boolean).join(' ').trim(),
      client_phone:
        doc.client?.phones?.find((phone) => phone.isPrimary)?.phone || doc.client?.phone || '',
      products: (doc.products || []).map((row: any) => ({
        quan: Number(row.quan || 0),
        price: Number(row.price || 0),
        product: {
          id: row.product?.id,
          description: row.description ?? row.product?.description ?? '',
          brand: row.brand ?? row.product?.brand?.description ?? '',
          article: row.article ?? row.product?.article ?? '',
          quan: Number(row.quan || 0),
          price: Number(row.price || 0)
        }
      }))
    };
  }

  private buildProductLabelRequest(payload: ProductLabelPrintDto | PriceTagDto) {
    const warehouseId = Number(payload.warehouseId || this.user?.defaultWarehouse?.id || 0) || null;
    const productId =
      Number(
        (payload as PriceTagDto).productId || (payload as ProductLabelPrintDto).productId || 0
      ) || this.tryParseNumericId(payload.barcode);

    return {
      productId: productId || undefined,
      barcode: payload.barcode ? String(payload.barcode) : undefined,
      copies: Math.max(
        1,
        Number(
          payload.copies ??
            (payload as PriceTagDto).quan ??
            (payload as ProductLabelPrintDto).quanPrint ??
            1
        )
      ),
      warehouseId: warehouseId || undefined,
      labelProfileId: this.resolveLabelProfileId(payload),
      labelProfileCode: this.resolveLabelProfileCode(payload),
      labelProfileMode: payload.labelProfileMode || undefined,
      target: this.buildLabelTargetSelector(payload, warehouseId)
    };
  }

  private buildCellLabelRequest(payload: CellLabelDto) {
    const warehouseId = Number(payload.warehouseId || this.user?.defaultWarehouse?.id || 0) || null;
    return {
      cellId: Number(payload.cellId || 0) || undefined,
      cellCode:
        String(payload.cellCode || payload.cell || '')
          .trim()
          .replace(/^!+/, '') || undefined,
      copies: Math.max(1, Number(payload.copies ?? payload.quan ?? 1)),
      warehouseId: warehouseId || undefined,
      labelProfileId: this.resolveLabelProfileId(payload),
      labelProfileCode: this.resolveLabelProfileCode(payload),
      labelProfileMode: payload.labelProfileMode || undefined,
      target: this.buildLabelTargetSelector(payload, warehouseId)
    };
  }

  private buildLabelProfilePreviewPayload(profile: LabelProfileRef | CreateLabelProfileDto) {
    return {
      code: profile.code || 'preview',
      name: profile.name || 'Preview',
      widthMm: Number(profile.widthMm || 58),
      heightMm: Number(profile.heightMm || 40),
      gapMm: Number(profile.gapMm ?? 2),
      direction: Number(profile.direction || 0),
      speed: Number(profile.speed || 2),
      density: Number(profile.density || 8),
      characterEncoding: profile.characterEncoding || 'windows-1251',
      codePage: profile.codePage ?? '1251',
      layoutJson: profile.layoutJson || null
    };
  }

  private resolveLabelProfileId(payload: {
    profileId?: number | string | null;
    labelProfileMode?: string | null;
  }) {
    if (payload.labelProfileMode === 'printer_target') {
      return null;
    }
    return (
      payload.profileId ??
      this.user?.defaultLabelProfile?.profileId ??
      this.user?.defaultLabelProfile?.id ??
      null
    );
  }

  private resolveLabelProfileCode(payload: {
    profileCode?: string | null;
    labelProfileMode?: string | null;
  }) {
    if (payload.labelProfileMode === 'printer_target') {
      return null;
    }
    return (
      payload.profileCode ??
      this.user?.defaultLabelProfile?.profileCode ??
      this.user?.defaultLabelProfile?.code ??
      null
    );
  }

  private buildLabelTargetSelector(
    payload: {
      target?: PrintTargetSelector | null;
      agentName?: string | null;
      agentDeviceId?: string | null;
    },
    warehouseId: number | null
  ): PrintTargetSelector | undefined {
    if (payload.target) {
      return {
        ...payload.target,
        warehouseId: payload.target.warehouseId ?? warehouseId ?? undefined
      };
    }

    if (payload.agentDeviceId || payload.agentName) {
      return {
        agentDeviceId: payload.agentDeviceId ?? null,
        agentName: payload.agentName ?? null,
        warehouseId: warehouseId ?? undefined,
        strategy: 'explicit_agent'
      };
    }

    if (warehouseId) {
      return {
        warehouseId,
        strategy: 'warehouse_online'
      };
    }

    return undefined;
  }

  private tryParseNumericId(value?: string | number | null) {
    const normalized = String(value ?? '').trim();
    if (!/^\d+$/.test(normalized)) {
      return 0;
    }
    return Number(normalized);
  }
}
